import { SCENE_HEIGHT, SCENE_WIDTH } from './minerArt.js';

/**
 * PXC mine — shared paint (docs/ART-DIRECTION.md).
 *
 * Direction: "cut & vein". The world is built from the same material language
 * as the product chrome — brushed metal-blue planes, a bright rule along the
 * upper-left of every solid, a dark lower-right, a 3px dot grain over every
 * surface, and gold reserved for value. Nothing is outlined; volume comes from
 * value steps only.
 *
 * One key (warm lamp amber, upper-left), one fill (cool mineral teal, lower
 * right), one rim (pale gold on the key-facing contour), applied identically to
 * rock, timber, steel and characters. That is what makes hand-authored art read
 * as one universe.
 */
export function MineDefs() {
  return (
    <defs>
      {/* ---- rock, by depth band ------------------------------------------ */}
      <linearGradient id="pxc-rock-far" x1="0.2" y1="0" x2="0.7" y2="1">
        <stop offset="0" stopColor="#111A26" />
        <stop offset="0.55" stopColor="#0B121B" />
        <stop offset="1" stopColor="#070C12" />
      </linearGradient>

      <linearGradient id="pxc-rock-mid" x1="0.15" y1="0" x2="0.8" y2="1">
        <stop offset="0" stopColor="#2A3648" />
        <stop offset="0.5" stopColor="#1C2635" />
        <stop offset="1" stopColor="#141C27" />
      </linearGradient>

      <linearGradient id="pxc-rock-near" x1="0.1" y1="0" x2="0.75" y2="1">
        <stop offset="0" stopColor="#3E4E63" />
        <stop offset="0.48" stopColor="#2C3849" />
        <stop offset="1" stopColor="#1D2634" />
      </linearGradient>

      <linearGradient id="pxc-rock-fore" x1="0.1" y1="0" x2="0.7" y2="1">
        <stop offset="0" stopColor="#212B39" />
        <stop offset="0.5" stopColor="#141B26" />
        <stop offset="1" stopColor="#0A0E15" />
      </linearGradient>

      {/* The lit facet. Only painted on planes that face the lamps. */}
      <linearGradient id="pxc-rock-lit" x1="0" y1="0" x2="0.55" y2="1">
        <stop offset="0" stopColor="#6E86A2" />
        <stop offset="1" stopColor="#38465A" />
      </linearGradient>

      <linearGradient id="pxc-floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#45566C" />
        <stop offset="0.26" stopColor="#2E3A4A" />
        <stop offset="1" stopColor="#171F2A" />
      </linearGradient>

      {/* ---- materials ---------------------------------------------------- */}
      <linearGradient id="pxc-timber" x1="0" y1="0" x2="1" y2="0.35">
        <stop offset="0" stopColor="#8A6741" />
        <stop offset="0.32" stopColor="#6B4E33" />
        <stop offset="1" stopColor="#3E2C1D" />
      </linearGradient>

      <linearGradient id="pxc-steel" x1="0" y1="0" x2="0.65" y2="1">
        <stop offset="0" stopColor="#68809B" />
        <stop offset="0.42" stopColor="#3B4859" />
        <stop offset="1" stopColor="#232C39" />
      </linearGradient>

      <linearGradient id="pxc-iron" x1="0" y1="0" x2="0.7" y2="1">
        <stop offset="0" stopColor="#5A6674" />
        <stop offset="0.4" stopColor="#3D4753" />
        <stop offset="1" stopColor="#2A323C" />
      </linearGradient>

      {/* Gold is the product's value colour — ore only, nothing else. */}
      <linearGradient id="pxc-ore-gold" x1="0.1" y1="0" x2="0.9" y2="1">
        <stop offset="0" stopColor="#FBE7AE" />
        <stop offset="0.38" stopColor="#E4B75C" />
        <stop offset="0.74" stopColor="#B98A32" />
        <stop offset="1" stopColor="#8F6620" />
      </linearGradient>

      <linearGradient id="pxc-crystal-teal" x1="0.25" y1="0" x2="0.75" y2="1">
        <stop offset="0" stopColor="#DFFBFF" />
        <stop offset="0.32" stopColor="#66D6EC" />
        <stop offset="0.72" stopColor="#2A8FA6" />
        <stop offset="1" stopColor="#13454E" />
      </linearGradient>

      <linearGradient id="pxc-crystal-violet" x1="0.25" y1="0" x2="0.75" y2="1">
        <stop offset="0" stopColor="#E2D8FF" />
        <stop offset="0.34" stopColor="#9683F0" />
        <stop offset="1" stopColor="#2E2359" />
      </linearGradient>

      {/* ---- light -------------------------------------------------------- */}
      <radialGradient id="pxc-lamp-glow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#FFE3AE" stopOpacity="0.6" />
        <stop offset="0.4" stopColor="#E0A94F" stopOpacity="0.24" />
        <stop offset="1" stopColor="#C8912F" stopOpacity="0" />
      </radialGradient>

      {/* A real cone under each hanging lamp, so light has a direction. */}
      <linearGradient id="pxc-lamp-cone" x1="0.5" y1="0" x2="0.5" y2="1">
        <stop offset="0" stopColor="#FFDDA0" stopOpacity="0.28" />
        <stop offset="0.5" stopColor="#FFDDA0" stopOpacity="0.08" />
        <stop offset="1" stopColor="#FFDDA0" stopOpacity="0" />
      </linearGradient>

      <radialGradient id="pxc-key-light" cx="0.3" cy="0.16" r="0.8">
        <stop offset="0" stopColor="#F0C97A" stopOpacity="0.14" />
        <stop offset="0.5" stopColor="#F0C97A" stopOpacity="0.04" />
        <stop offset="1" stopColor="#F0C97A" stopOpacity="0" />
      </radialGradient>

      <radialGradient id="pxc-fill-light" cx="0.84" cy="0.88" r="0.62">
        <stop offset="0" stopColor="#2A5B63" stopOpacity="0.3" />
        <stop offset="1" stopColor="#2A5B63" stopOpacity="0" />
      </radialGradient>

      <linearGradient id="pxc-haze" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#0A0F16" stopOpacity="0.8" />
        <stop offset="0.5" stopColor="#0A0F16" stopOpacity="0.14" />
        <stop offset="1" stopColor="#0A0F16" stopOpacity="0" />
      </linearGradient>

      <radialGradient id="pxc-vignette" cx="0.5" cy="0.44" r="0.76">
        <stop offset="0.48" stopColor="#05070A" stopOpacity="0" />
        <stop offset="1" stopColor="#05070A" stopOpacity="0.8" />
      </radialGradient>

      <linearGradient id="pxc-tunnel-depth" x1="0.5" y1="0" x2="0.5" y2="1">
        <stop offset="0" stopColor="#040609" />
        <stop offset="0.62" stopColor="#080C12" />
        <stop offset="1" stopColor="#10161F" />
      </linearGradient>

      {/* ---- seating a raster character into a painted floor --------------
          A soft radial rather than a blurred ellipse: feGaussianBlur on six
          characters is a real cost on a mid-range phone, and a gradient buys
          the same softness for free. */}
      <radialGradient id="pxc-contact" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#000000" stopOpacity="0.62" />
        <stop offset="0.45" stopColor="#000000" stopOpacity="0.34" />
        <stop offset="1" stopColor="#000000" stopOpacity="0" />
      </radialGradient>

      {/* The light the painted lanterns would be throwing onto a figure that
          was really standing there. Without it a sprite reads as a sticker. */}
      <radialGradient id="pxc-lightwrap" cx="0.5" cy="0.42" r="0.5">
        <stop offset="0" stopColor="#FFCE86" stopOpacity="0.16" />
        <stop offset="0.6" stopColor="#FFAE55" stopOpacity="0.06" />
        <stop offset="1" stopColor="#FF9E3C" stopOpacity="0" />
      </radialGradient>

      {/* ---- signature: the dot grain, lifted from the product chrome ------ */}
      <pattern id="pxc-grain" width="7" height="7" patternUnits="userSpaceOnUse">
        <circle cx="1.6" cy="1.6" r="1.5" fill="#9FB6D0" opacity="0.05" />
        <circle cx="5.2" cy="4.8" r="1.1" fill="#05070A" opacity="0.07" />
      </pattern>

      <pattern id="pxc-grain-fine" width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="1.2" cy="1.2" r="1" fill="#B9CCE2" opacity="0.05" />
      </pattern>

      {/* Sized from minerArt so the clip follows the scene instead of a
          hard-coded height. Getting these out of sync silently cuts the
          bottom off the shaft. */}
      <clipPath id="pxc-scene-clip">
        <rect x="0" y="0" width={SCENE_WIDTH} height={SCENE_HEIGHT} />
      </clipPath>
    </defs>
  );
}
